import React from 'react'

/**
 * Displays comprehensive validation feedback including errors and warnings.
 * Designed to help developers and AI understand validation issues clearly.
 */
const ValidationFeedback = ({
  validationResult,
  isValidating = false
}) => {
  if (isValidating) {
    return (
      <div className="validation-feedback validation-feedback--validating" role="status">
        <div className="validation-feedback__header">
          <span className="validation-feedback__icon">⏳</span>
          <span className="validation-feedback__title">Validating EPL syntax…</span>
        </div>
      </div>
    )
  }

  if (!validationResult) {
    return null
  }

  const { valid, message, errors = [], warnings = [], validationType } = validationResult
  const hasWarnings = warnings.length > 0

  if (valid) {
    return (
      <div className="validation-feedback validation-feedback--success" role="status">
        <div className="validation-feedback__header">
          <span className="validation-feedback__icon">✓</span>
          <span className="validation-feedback__title">
            EPL syntax is valid{hasWarnings ? ` (${warnings.length} warning${warnings.length === 1 ? '' : 's'})` : ''}
          </span>
        </div>

        {hasWarnings && (
          <ul className="validation-feedback__compact-list">
            {warnings.map((warning, index) => (
              <li key={index} className="validation-feedback__compact-item">
                <span className="validation-feedback__compact-location">
                  Ln {warning.lineNumber}, Col {warning.columnNumber}:
                </span>
                <span className="validation-feedback__compact-message">{warning.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  // Invalid state - show errors and warnings
  const hasErrors = errors.length > 0

  return (
    <div className="validation-feedback validation-feedback--error" role="alert">
      <div className="validation-feedback__header">
        <span className="validation-feedback__icon">✕</span>
        <span className="validation-feedback__title">
          {message || 'Validation failed'} ({errors.length} error{errors.length === 1 ? '' : 's'}{hasWarnings ? `, ${warnings.length} warning${warnings.length === 1 ? '' : 's'}` : ''})
        </span>
      </div>

      {hasErrors && (
        <ul className="validation-feedback__compact-list">
          {errors.map((error, index) => (
            <li key={index} className="validation-feedback__compact-item validation-feedback__compact-item--error">
              <span className="validation-feedback__compact-location">
                Ln {error.lineNumber}, Col {error.columnNumber}:
              </span>
              <span className="validation-feedback__compact-message">{error.message}</span>
            </li>
          ))}
        </ul>
      )}

      {hasWarnings && (
        <ul className="validation-feedback__compact-list">
          {warnings.map((warning, index) => (
            <li key={index} className="validation-feedback__compact-item validation-feedback__compact-item--warning">
              <span className="validation-feedback__compact-location">
                Ln {warning.lineNumber}, Col {warning.columnNumber}:
              </span>
              <span className="validation-feedback__compact-message">{warning.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default ValidationFeedback
